import { Grid, Typography, Box, Chip, Paper } from '@mui/material';
import React from 'react';
import { makeStyles } from '@mui/styles';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import { QuotationItemRoomDistribution } from '../../types';

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(2),
		backgroundColor: '#fff3e0',
		borderRadius: theme.spacing(1),
		margin: theme.spacing(2, 0),
		border: '1px solid #ffcc02',
	},
	title: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		color: '#e65100',
		fontWeight: 'bold',
	},
	roomCard: {
		padding: theme.spacing(2),
		backgroundColor: 'white',
		borderRadius: theme.spacing(1),
		border: '1px solid #ffcc02',
		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
		transition: 'all 0.3s ease',
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
		},
	},
	roomHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
		paddingBottom: theme.spacing(1),
		borderBottom: '1px solid #fff3e0',
	},
	roomNumber: {
		fontWeight: 'bold',
		color: '#e65100',
		fontSize: '1rem',
	},
	occupancyContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
	},
	occupancyChip: {
		fontWeight: 'bold',
		'&.adults': {
			backgroundColor: '#e3f2fd',
			color: '#1976d2',
		},
		'&.children': {
			backgroundColor: '#f3e5f5',
			color: '#7b1fa2',
		},
		'&.infants': {
			backgroundColor: '#e8f5e8',
			color: '#388e3c',
		},
	},
	agesContainer: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1),
		backgroundColor: '#f5f5f5',
		borderRadius: theme.spacing(0.5),
	},
	agesText: {
		fontSize: '0.875rem',
		color: theme.palette.text.secondary,
		fontWeight: 500,
	},
}));

export interface QuotationListItemRoomDistributionProps {
	roomDistributions: QuotationItemRoomDistribution[];
	quotationId: number;
}

export const QuotationListItemRoomDistribution = ({
	roomDistributions,
	quotationId,
}: QuotationListItemRoomDistributionProps): JSX.Element => {
	const classes = useStyles();

	if (!roomDistributions || roomDistributions.length === 0) {
		return <></>;
	}

	return (
		<Box className={classes.container}>
			<Typography className={classes.title} variant="h6">
				<HotelIcon />
				Distribución de Habitaciones
			</Typography>

			<Grid container spacing={2}>
				{roomDistributions.map((room, index) => (
					<Grid item xs={12} md={6} lg={4} key={`quotation-${quotationId}-room-${index}`}>
						<Paper className={classes.roomCard}>
							<Box className={classes.roomHeader}>
								<HotelIcon color="primary" />
								<Typography className={classes.roomNumber}>Habitación {index + 1}</Typography>
							</Box>

							<Box className={classes.occupancyContainer}>
								{room.totalAdults > 0 && (
									<Chip
										className={`${classes.occupancyChip} adults`}
										icon={<PersonIcon />}
										label={`${room.totalAdults} Adulto${room.totalAdults > 1 ? 's' : ''}`}
										size="small"
									/>
								)}

								{room.totalChildren > 0 && (
									<Chip
										className={`${classes.occupancyChip} children`}
										icon={<ChildCareIcon />}
										label={`${room.totalChildren} Niño${room.totalChildren > 1 ? 's' : ''}`}
										size="small"
									/>
								)}

								{room.totalInfants > 0 && (
									<Chip
										className={`${classes.occupancyChip} infants`}
										icon={<BabyChangingStationIcon />}
										label={`${room.totalInfants} Infante${room.totalInfants > 1 ? 's' : ''}`}
										size="small"
									/>
								)}
							</Box>

							{room.childrenAges.length > 0 && (
								<Box className={classes.agesContainer}>
									<Typography className={classes.agesText}>
										<strong>Edades de niños:</strong> {room.childrenAges.join(', ')} años
									</Typography>
								</Box>
							)}

							{room.infantsAges.length > 0 && (
								<Box className={classes.agesContainer}>
									<Typography className={classes.agesText}>
										<strong>Edades de infantes:</strong> {room.infantsAges.join(', ')} años
									</Typography>
								</Box>
							)}
						</Paper>
					</Grid>
				))}
			</Grid>
		</Box>
	);
};
